import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { ReactNode } from 'react';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { WizardStepper, type WizardStep } from '../../../shared/components/WizardStepper';

interface OnboardingWizardShellProps {
  title: string;
  subtitle?: string;
  /** The whole journey, not only the steps this wizard renders — later ones live on the validation page. */
  steps: WizardStep[];
  activeStep: number;
  /** Whether the current step's inputs pass validation — gates the primary button. */
  canProceed: boolean;
  onBack: () => void;
  onNext: () => void;
  /** Primary button wording and icon; "Next" by default. */
  nextLabel?: string;
  nextIcon?: ReactNode;
  /** Locks navigation while the project is being created. */
  busy?: boolean;
  error: string | null;
  onClearError: () => void;
  children: ReactNode;
}

/**
 * Layout shared by every onboarding wizard: title, the journey's progress
 * line, the current step's content, and Back / primary action. Back is
 * hidden on the first step.
 */
export function OnboardingWizardShell({
  title,
  subtitle,
  steps,
  activeStep,
  canProceed,
  onBack,
  onNext,
  nextLabel = 'Next',
  nextIcon = <ArrowForwardIcon />,
  busy = false,
  error,
  onClearError,
  children,
}: OnboardingWizardShellProps) {
  const isFirst = activeStep === 0;

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h5" component="h1" fontWeight={800} letterSpacing="-0.01em">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      <WizardStepper steps={steps} activeStep={activeStep} />

      {error && (
        <Alert severity="error" onClose={onClearError}>
          {error}
        </Alert>
      )}

      <GlassPanel sx={{ p: { xs: 2, sm: 3 } }}>
        {/* noValidate: URL fields are validated (and normalised) by the steps
            themselves, so the browser's own "enter a URL" bubble would only get in the way. */}
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (canProceed && !busy) onNext();
          }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {children}

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={1.5}
            justifyContent={isFirst ? 'flex-end' : 'space-between'}
            sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
          >
            {!isFirst && (
              <Button type="button" variant="text" startIcon={<ArrowBackIcon />} disabled={busy} onClick={onBack}>
                Back
              </Button>
            )}
            <Button type="submit" variant="contained" size="large" endIcon={nextIcon} disabled={!canProceed || busy}>
              {nextLabel}
            </Button>
          </Stack>
        </Box>
      </GlassPanel>
    </Box>
  );
}
